"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/i18n";

interface NewsletterInlineProps {
    label?: string;
    compact?: boolean;
}

const GENERIC_SUBSCRIPTION_ERROR = "Unable to subscribe right now. Please try again in a moment.";

export function NewsletterInline({ label, compact = false }: NewsletterInlineProps) {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const { t } = useTranslation();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setErrorMessage(t.newsletter.emailError);
            setStatus("error");
            return;
        }

        setStatus("loading");
        setErrorMessage("");

        try {
            const res = await fetch("/api/newsletter", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => null);
                const safeMessage =
                    res.status < 500 && typeof data?.error === "string"
                        ? data.error
                        : t.newsletter.genericError;

                throw new Error(safeMessage);
            }

            setStatus("success");
            setEmail("");
        } catch (err) {
            setErrorMessage(
                err instanceof Error && err.message
                    ? err.message
                    : t.newsletter.genericError
            );
            setStatus("error");
        }
    };

    if (status === "success") {
        return (
            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center gap-1 text-center"
            >
                <p className={compact ? "text-sm font-medium text-text" : "text-base font-medium text-text"}>
                    {t.newsletter.inlineSuccess}
                </p>
                <p className="text-xs text-text-muted">
                    {t.newsletter.inlineSuccessSpam}
                </p>
            </motion.div>
        );
    }

    return (
        <div>
            {label && !compact && (
                <p className="text-xs uppercase tracking-[0.12em] font-semibold text-text-muted mb-3">
                    {label}
                </p>
            )}
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 w-full max-w-sm">
                {/* Honeypot */}
                <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    className="absolute opacity-0 pointer-events-none h-0 w-0"
                    aria-hidden="true"
                />
                <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (status === "error") setStatus("idle");
                    }}
                    placeholder={t.newsletter.placeholder}
                    className={`flex-1 px-3 rounded-lg bg-surface border border-border text-text text-sm placeholder:text-text-muted focus:border-mars focus:outline-none transition-colors ${compact ? "py-2" : "py-2.5"}`}
                    required
                    aria-label="Email address for newsletter"
                />
                <button
                    type="submit"
                    disabled={status === "loading"}
                    className={`px-4 rounded-lg bg-mars text-white text-sm font-medium hover:bg-mars-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap ${compact ? "py-2" : "py-2.5"}`}
                >
                    {status === "loading" ? "..." : t.newsletter.inlineButton}
                </button>
            </form>
            <AnimatePresence>
                {status === "error" && errorMessage && (
                    <motion.p
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="text-xs text-red-400 mt-2"
                        role="alert"
                    >
                        {errorMessage}
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    );
}
